/**
 * pages/admin-periods.ts —— 管理后台 · 周期 Tab（W2-2）
 * 来历：一期 [SEC:page-admin] / [SEC:page-admin-write] 的周期部分，渲染层重写、交互与版式不变。
 *
 * 与一期最大的两处差别：
 * 1. **状态、提交率、逾期支数、能不能编辑 / 删除、新建默认区间，全部是服务端算好下发的**
 *    （`GET /api/periods/admin`）。一期这些都在前端现算，二期一个都不许算（红线 4、前端纪律 2）。
 *    —— 尤其是新建表单的默认起止：前端算就要 `new Date()`，一 new 就掉进浏览器本地时区。
 * 2. **「一期都还没建」是正常态**：一期 CYCLES 恒有 8 期，二期 period 表上线时是空的，
 *    必须有空态并把「新建周期」摆在空态里（红线 3.5）。
 *
 * ⚠ 写操作后一律 `await meta.refresh()` 再 `refreshCurrent()`（本文件的 afterWrite 已经收口）：
 *   周期变了但 meta 没刷，路由守卫会拿旧列表判定，双周报页的期号会被判非法（meta.ts 纪律 2）。
 */
import type { Period, PeriodAdminItem, PeriodAdminView } from '@shared-types';
import { periods as periodApi } from '../api/index.js';
import { errText } from '../api/client.js';
import { closeModal, confirmModal, emptyHtml, openModal, toast } from '../shared/common.js';
import { esc } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { afterWrite } from './admin-shared.js';

const ST_LABEL = { ended: '已结束', current: '进行中', future: '未开始' } as const;

/* ===================== 渲染（纯函数） ===================== */

/**
 * 备注列：提交率 +（已结束时）逾期未补交支数。
 * ⚠ 未开始的期显示「—」——还没开始，谈提交率没有意义（一期 cycleNoteHtml 同口径）。
 * ⚠ 这里只读服务端下发的数，一个百分比都不在前端算。
 */
function noteHtml(it: PeriodAdminItem): string {
  if (!it.rate) return '<span class="dash">—</span>';
  const { submitted, expected, pct } = it.rate;
  const rate =
    `提交率 <b class="num">${pct}%</b>（<span class="num">${submitted}</span> / <span class="num">${expected}</span>）`;
  const owed = it.owed ? ` · <span class="warn-txt">逾期未补交 ${it.owed} 支</span>` : '';
  return rate + owed;
}

function rowHtml(it: PeriodAdminItem): string {
  const p = it.period;
  const ops =
    (it.canEdit ? `<button class="tbtn" data-adm="cyedit|${esc(p.id)}">编辑</button>` : '') +
    (it.canDelete ? `<button class="tbtn danger" data-adm="cydrop|${esc(p.id)}">删除</button>` : '');
  const cls = it.state === 'current' ? 'cur' : it.state === 'ended' ? 'done' : '';
  return `
  <tr data-cy="${esc(p.id)}" class="${cls}">
    <td><span class="cyname">${esc(p.name)}</span></td>
    <td><span class="num">${esc(p.start)}</span> ~ <span class="num">${esc(p.end)}</span></td>
    <td><span class="num">${esc(p.due)}</span></td>
    <td><span class="badge cy-${it.state}">${ST_LABEL[it.state]}</span></td>
    <td class="cy-hint">${noteHtml(it)}</td>
    <td><span class="ops">${ops}</span></td>
  </tr>`;
}

function periodsPanelHtml(v: PeriodAdminView): string {
  /* 🔴 空态：period 表上线时是空的，第 1 期由管理员在这里建出来（红线 3.5）。
     一期 CYCLES 恒有 8 期、永远走不到这个分支，【不要照搬一期的写法】。 */
  if (!v.items.length) {
    return emptyHtml(
      EIC.cal,
      '尚未创建任何周期',
      '双周报按周期提交，先创建第 1 期。周期两周一期，起止日期可按实际排期调整，期与期之间允许留空当。',
      `<button class="btn primary" data-adm="cynew">${IC.plus}新建周期</button>`,
    );
  }
  return `
  <table class="matrix at">
    <colgroup><col class="cy-no"><col class="cy-span"><col class="cy-due">
      <col class="cy-st"><col class="cy-note"><col class="cy-op"></colgroup>
    <thead><tr>
      <th>期号</th><th>周期起止</th><th>提交截止</th><th>状态</th><th>备注</th><th>操作</th>
    </tr></thead>
    <tbody>${v.items.map(rowHtml).join('')}</tbody>
  </table>`;
}

function toolbarHtml(v: PeriodAdminView): string {
  /* ⚠ needNextAlert 由服务端判（当前期结束前 3 天仍未建下一期）。
     「临近再建」的代价就是容易忘建，这条提醒是唯一的补救（types/period.ts 六处空态之一）。 */
  const note = v.needNextAlert
    ? `<span class="ph-note" style="max-width:none"><span class="warn-txt">当前周期即将结束，下一期还没有创建。</span>周期结束后系统会进入没有进行中周期的空当态，双周报无法填报。</span>`
    : `<span class="ph-note" style="max-width:none">周期两周一期，起止日期按实际排期填写（会跳过国庆与年末，允许留空当）。提交截止恒为周期结束当日 24:00，不单独设置。</span>`;
  return `
  <div class="toolbar">
    ${note}
    <span class="cnt">共 <span class="num">${v.items.length}</span> 期</span>
    <button class="btn primary" data-adm="cynew">${IC.plus}新建周期</button>
  </div>`;
}

/* ===================== 写操作 ===================== */

/**
 * 新建 / 编辑周期共用一个弹窗（`it` 为 null 表示新建）。
 * ⚠ 表单里【没有「提交截止」输入框】：截止恒 = 结束当日 24:00（2026-08-05 管理员明确），
 *   这里只读显示，随结束日期实时联动——否则表单上会出现两个对不上的日期。
 * ⚠ 期号只读：由服务端生成，创建后不可改。
 */
function periodModal(v: PeriodAdminView, it: PeriodAdminItem | null): void {
  const isNew = it === null;
  const name = isNew ? v.suggest.name : it.period.name;
  const init = isNew
    ? { start: v.suggest.start, end: v.suggest.end }
    : { start: it.period.start, end: it.period.end };

  const read = (root: ParentNode) => ({
    start: root.querySelector<HTMLInputElement>('#cy-start')?.value ?? '',
    end: root.querySelector<HTMLInputElement>('#cy-end')?.value ?? '',
  });

  const body = `
    <div class="fld"><label>期号</label>
      <input type="text" readonly value="${esc(name)}" aria-label="期号">
      <div class="hint">${
        isNew
          ? '取现有最大期号 + 1，由系统生成，创建后不可修改。'
          : '期号创建后不可修改。'
      }</div></div>
    <div class="fld" data-f="span"><label>周期起止<span class="req">*</span></label>
      <div class="fld-2">
        <input type="date" id="cy-start" value="${esc(init.start)}" aria-label="周期起始">
        <input type="date" id="cy-end" value="${esc(init.end)}" aria-label="周期结束">
      </div>
      <div class="hint">${
        isNew ? '默认接在最后一期之后、两周一期，可改。' : '两周一期，可改。'
      }结束日期须晚于起始日期，且不得与相邻期重叠——真实排期会跳过国庆与年末，<b>允许与上一期之间留空当</b>。</div>
      <div class="err"></div></div>
    <div class="fld"><label>提交截止</label>
      <input type="text" readonly id="cy-duetip" value="${esc(init.end)} 24:00" aria-label="提交截止">
      <div class="hint"><b>不单独设置</b>：截止时间恒为周期结束当日 24:00，随结束日期自动变化。${
        it && it.state === 'current'
          ? '<br>周期未结束时全营均无逾期；已提交队伍的逾期时长是提交那一刻的快照，改结束日期不重算。'
          : ''
      }</div></div>`;

  const rec = openModal({
    title: isNew ? '新建周期' : `编辑${name}`,
    body,
    footer: `<button class="btn" data-mclose>取消</button>
            <button class="btn primary" data-save>${isNew ? '创建周期' : '保存修改'}</button>`,
    dirty() {
      const val = read(rec.mask);
      return val.start !== init.start || val.end !== init.end;
    },
    onMount(root) {
      const end = root.querySelector<HTMLInputElement>('#cy-end');
      const due = root.querySelector<HTMLInputElement>('#cy-duetip');
      const syncDue = () => {
        if (due && end) due.value = (end.value || '—') + ' 24:00';
      };
      end?.addEventListener('change', syncDue);
      end?.addEventListener('input', syncDue);
      root.querySelector('[data-save]')?.addEventListener('click', () => void submit(root));
      root.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT') {
          ev.preventDefault();
          void submit(root);
        }
      });
    },
  });

  /** 把一句错误落到起止日期那一格上（服务端文案原样显示，不在前端另写一套） */
  function markSpan(root: ParentNode, msg: string): void {
    const f = root.querySelector('[data-f="span"]');
    if (!f) return;
    f.classList.add('bad');
    const box = f.querySelector('.err');
    if (box) box.textContent = msg;
    f.querySelector<HTMLInputElement>('input')?.focus();
  }

  async function submit(root: ParentNode): Promise<void> {
    const val = read(root);
    root.querySelectorAll('.fld').forEach((f) => f.classList.remove('bad'));

    /* ⚠ 前端只拦「明显填错」这两条，**不是权限也不是口径**：
       重叠校验、期号生成、已结束不可改都在服务端（红线 4），失败文案直接用服务端返回的。
       日期比较用 'YYYY-MM-DD' 字符串，字典序即时间序，不许 new Date()。 */
    if (!val.start || !val.end) return markSpan(root, '请填写完整的周期起止日期');
    if (val.end <= val.start) return markSpan(root, '结束日期须晚于起始日期');

    const btn = root.querySelector<HTMLButtonElement>('[data-save]');
    if (btn) btn.disabled = true;
    try {
      const saved: Period = isNew
        ? await periodApi.createPeriod({ start: val.start, end: val.end })
        : await periodApi.updatePeriod(it.period.id, { start: val.start, end: val.end });
      closeModal(true);
      await afterWrite(
        isNew
          ? `已新建${saved.name}（${saved.start} ~ ${saved.end}，截止 ${saved.end} 24:00）`
          : `已更新${saved.name}的周期日程`,
      );
    } catch (e) {
      // ⚠ 错误留在弹窗里，不 toast：填的内容还在，人要在原地改
      markSpan(root, errText(e));
    } finally {
      if (btn) btn.disabled = false;
    }
  }
}

function dropPeriod(it: PeriodAdminItem): void {
  const p = it.period;
  confirmModal({
    title: '删除周期',
    okText: '确认删除',
    danger: true,
    html: `确认删除 <b>${esc(p.name)}</b>（${esc(p.start)} ~ ${esc(p.end)}）？<br>
      该周期尚未开始、也没有任何双周报数据。删除后期号 ${p.no} 会空出来，下次新建仍从这个号开始。`,
    onOk: () => {
      void (async () => {
        try {
          await periodApi.deletePeriod(p.id);
          await afterWrite(`已删除${p.name}`);
        } catch (e) {
          // 删除是一次性动作，没有可停留的表单，用 toast 报错
          toast(errText(e));
        }
      })();
    },
  });
}

/* ===================== 对外（供 pages/admin.ts 装配） ===================== */

export const load = (signal: AbortSignal): Promise<PeriodAdminView> => periodApi.adminView({ signal });

/** 工具条：一期都没建时不出（那时「新建周期」摆在空态里） */
export const toolbar = (v: PeriodAdminView): string => (v.items.length ? toolbarHtml(v) : '');

export const panel = (v: PeriodAdminView): string => `<div class="panel" id="adm-panel">${periodsPanelHtml(v)}</div>`;

/** 本 Tab 的点击分支。⚠ 只认 data-ad，Tab 切换由 shell 统一处理 */
export function onClick(t: Element, v: PeriodAdminView): void {
  const act = t.closest<HTMLElement>('[data-adm]')?.dataset.adm;
  if (!act) return;
  const [cmd, id] = act.split('|');
  if (cmd === 'cynew') return periodModal(v, null);

  const it = v.items.find((x) => x.period.id === id);
  if (!it) return;
  if (cmd === 'cyedit') return periodModal(v, it);
  if (cmd === 'cydrop') return dropPeriod(it);
}
